import { useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { Download, Search } from "lucide-react";
import { listClips, saveClip, deleteClip, rewriteClip } from "../lib/api";
import ClipCard from "../components/ClipCard";

const PLATFORMS = ["all", "tiktok", "reels", "shorts"];
const APPROVALS = ["all", "approved", "needs_rewrite", "rejected"];

export const Library = () => {
    const [clips, setClips] = useState([]);
    const [search, setSearch] = useState("");
    const [platform, setPlatform] = useState("all");
    const [approval, setApproval] = useState("all");
    const [savedOnly, setSavedOnly] = useState(false);
    const [loading, setLoading] = useState(true);
    const [rewritingId, setRewritingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (platform !== "all") params.platform = platform;
            if (approval !== "all") params.approval = approval;
            if (savedOnly) params.saved = true;
            if (search) params.search = search;
            const data = await listClips(params);
            setClips(data);
        } catch (e) {
            toast.error("Failed to load library");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [platform, approval, savedOnly]);

    const stats = useMemo(() => {
        const total = clips.length;
        const approved = clips.filter((c) => c.overall_approval === "approved").length;
        const rewrite = clips.filter((c) => c.overall_approval === "needs_rewrite").length;
        const rejected = clips.filter((c) => c.overall_approval === "rejected").length;
        return { total, approved, rewrite, rejected };
    }, [clips]);

    const exportJSON = () => {
        const blob = new Blob([JSON.stringify(clips, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `dark8s-library-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const onSave = async (clip) => {
        await saveClip(clip.id, !clip.saved);
        setClips((arr) => arr.map((c) => (c.id === clip.id ? { ...c, saved: !c.saved } : c)));
    };
    const onDelete = async (clip) => {
        try {
            await deleteClip(clip.id);
            setClips((arr) => arr.filter((c) => c.id !== clip.id));
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Delete failed");
        }
    };
    const onRewrite = async (clip) => {
        setRewritingId(clip.id);
        try {
            const updated = await rewriteClip(clip.id);
            setClips((arr) => arr.map((c) => (c.id === clip.id ? updated : c)));
        } catch (e) {
            toast.error("Rewrite failed");
        } finally {
            setRewritingId(null);
        }
    };

    return (
        <div data-testid="library-page" className="min-h-screen">
            <Toaster theme="dark" position="top-right" />
            <section className="border-b border-[#262626] px-6 md:px-12 py-10 noise-bg">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div>
                        <div className="d8-label mb-3">/ Library</div>
                        <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-5xl leading-none">
                            Archive
                        </h1>
                        <p className="mt-4 text-[#8a8a8a] max-w-xl">
                            All generated and saved clips. Filter by platform, approval state, or search the caption stream.
                        </p>
                    </div>
                    <button
                        data-testid="export-json"
                        onClick={exportJSON}
                        className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#ff2a2a] text-[#f5f5f0] font-heading font-bold uppercase tracking-wide px-5 py-3 transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        Export JSON
                    </button>
                </div>

                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#262626] border border-[#262626]">
                    {[
                        ["Total", stats.total],
                        ["Approved", stats.approved],
                        ["Needs rewrite", stats.rewrite],
                        ["Rejected", stats.rejected],
                    ].map(([k, v]) => (
                        <div key={k} className="bg-[#0a0a0a] p-4">
                            <div className="d8-label mb-1">{k}</div>
                            <div className="font-mono-d8 text-2xl text-[#f5f5f0] tabular-nums">{v}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="px-6 md:px-12 py-8 border-b border-[#262626]">
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a8a]" />
                        <input
                            data-testid="library-search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                            placeholder="Search title or caption…"
                            className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm pl-10 pr-3 py-3 focus:outline-none focus:border-[#ff2a2a]"
                        />
                    </div>
                    <select
                        data-testid="filter-platform"
                        value={platform}
                        onChange={(e) => setPlatform(e.target.value)}
                        className="bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-wider p-3"
                    >
                        {PLATFORMS.map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                    <select
                        data-testid="filter-approval"
                        value={approval}
                        onChange={(e) => setApproval(e.target.value)}
                        className="bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-wider p-3"
                    >
                        {APPROVALS.map((p) => (
                            <option key={p} value={p}>{p.replace(/_/g, " ")}</option>
                        ))}
                    </select>
                    <label className="flex items-center gap-2 border border-[#262626] px-3 py-3 cursor-pointer hover:bg-[#121212]">
                        <input
                            data-testid="filter-saved"
                            type="checkbox"
                            checked={savedOnly}
                            onChange={(e) => setSavedOnly(e.target.checked)}
                            className="accent-[#ff2a2a]"
                        />
                        <span className="font-mono-d8 text-xs uppercase tracking-wider text-[#f5f5f0]">Saved only</span>
                    </label>
                </div>
            </section>

            <section className="p-6 md:p-12">
                {loading ? (
                    <div data-testid="library-loading" className="d8-label">Loading archive…</div>
                ) : clips.length === 0 ? (
                    <div
                        data-testid="library-empty"
                        className="border border-dashed border-[#262626] p-12 text-center"
                    >
                        <div className="d8-label mb-2">No clips match</div>
                        <p className="text-[#8a8a8a] text-sm">Try clearing filters or generating some.</p>
                    </div>
                ) : (
                    <div data-testid="library-grid" className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                        {clips.map((c) => (
                            <ClipCard
                                key={c.id}
                                clip={c}
                                onSave={onSave}
                                onDelete={onDelete}
                                onRewrite={onRewrite}
                                rewriting={rewritingId === c.id}
                            />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default Library;
